import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Sparkles,
  CalendarPlus,
  Wand2,
  CalendarCheck,
  Users,
  MessageSquare,
  Inbox,
  Ticket,
  Mail,
  ClipboardList,
  HelpCircle,
  Lightbulb,
  CheckCircle2,
  AlertTriangle,
  KeyRound,
  Layers,
  UserCog,
  ChevronRight,
  MessageCircle,
} from "lucide-react";

// LINE 爆速サポート URL
const LINE_SUPPORT_URL = "https://lin.ee/z4KZWkr";

/* =========================================================
 * 共通: Step アイテム / 説明セクション
 * ========================================================= */
function StepList({ items }) {
  return (
    <ol className="space-y-2.5">
      {items.map((it, i) => (
        <li key={i} className="flex gap-3" data-testid={`help-step-${i + 1}`}>
          <span className="shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">
            {i + 1}
          </span>
          <div className="flex-1">
            <p className="text-sm text-slate-800 leading-relaxed">{it}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

function Section({ icon: Icon, title, children, testId }) {
  return (
    <section className="space-y-2" data-testid={testId}>
      <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
        <Icon className="w-4 h-4 text-blue-600" />
        {title}
      </h3>
      <div className="text-sm text-slate-700 leading-relaxed space-y-2 pl-6">
        {children}
      </div>
    </section>
  );
}

function TipBox({ children, tone = "blue" }) {
  const tones = {
    blue: "bg-blue-50 border-blue-200 text-blue-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    rose: "bg-rose-50 border-rose-200 text-rose-900",
  };
  const Icon = tone === "amber" ? AlertTriangle : tone === "rose" ? AlertTriangle : tone === "emerald" ? CheckCircle2 : Lightbulb;
  return (
    <div className={`border rounded-md px-3 py-2 text-xs flex gap-2 ${tones[tone]}`}>
      <Icon className="w-4 h-4 shrink-0 mt-0.5" />
      <div className="flex-1 leading-relaxed">{children}</div>
    </div>
  );
}

function HiddenTip({ id, num, title, body, where }) {
  return (
    <div
      id={id}
      data-testid={`hidden-tip-${id}`}
      className="bg-white rounded-lg border border-amber-200 p-3 sm:p-4 scroll-mt-20 transition-shadow"
    >
      <div className="flex items-start gap-3">
        <span className="shrink-0 w-7 h-7 rounded-full bg-amber-500 text-white text-xs font-bold flex items-center justify-center">
          {num}
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-slate-900 leading-snug">{title}</h3>
          <div className="text-xs text-slate-700 mt-1.5 leading-relaxed">{body}</div>
          {where && (
            <div className="mt-2 inline-flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
              <ChevronRight className="w-3 h-3" />{where}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* =========================================================
 * LINE 爆速サポート
 * ========================================================= */
function LineSupportBanner({ compact = false }) {
  if (compact) {
    return (
      <a
        href={LINE_SUPPORT_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="line-support-link"
        className="inline-flex items-center gap-1.5 text-xs font-bold text-[#06C755] hover:text-[#05A847]"
      >
        <MessageCircle className="w-3.5 h-3.5" />
        LINEで質問する
      </a>
    );
  }
  return (
    <a
      href={LINE_SUPPORT_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="line-support-banner"
      className="block rounded-xl border-2 border-[#06C755]/30 bg-gradient-to-br from-[#06C755]/10 to-[#06C755]/5 hover:from-[#06C755]/15 hover:to-[#06C755]/10 p-3 transition-all hover:-translate-y-0.5"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-[#06C755] flex items-center justify-center shrink-0">
          <MessageCircle className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold text-[#06C755] tracking-widest">LINE 爆速サポート</div>
          <p className="text-sm font-bold text-slate-900 leading-tight mt-0.5">
            営業時間中はその場で回答
          </p>
          <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
            操作で迷ったら気軽に LINE してください
          </p>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-400 shrink-0 self-center" />
      </div>
    </a>
  );
}

/* =========================================================
 * マネージャ向けマニュアル
 * ========================================================= */
function ManagerGuide() {
  return (
    <div className="space-y-8" data-testid="help-manager">
      {/* 管理者(店長)設定の流れ + LINE 爆速サポート */}
      <Card className="overflow-hidden border-slate-200" data-testid="help-setup-flow">
        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr]">
          {/* 左: ステップ一覧 */}
          <div className="bg-gradient-to-br from-blue-50 to-emerald-50 p-5 lg:p-6 border-r border-slate-100">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-blue-600" />
              <div className="text-[11px] font-bold text-blue-600 tracking-widest">SETUP FLOW</div>
            </div>
            <h2 className="text-lg font-bold text-slate-900 leading-snug mb-4">管理者(店長)設定の流れ</h2>
            <ol className="space-y-2.5">
              {[
                { title: "パスワードをわかりやすいものに変更", note: "「アカウント設定」→「パスワード変更」から" },
                { title: "配置マスタを編集（必要な場合）", note: "「配置マスタ」で自店舗の配置に合わせて編集" },
                { title: "スタッフを登録する", note: "「スタッフ管理」→「新規登録」、ニックネームOK" },
                { title: "スタッフに登録依頼を送る", note: "発行されたID/PWを LINE / SMS でそのまま送信" },
                { title: "月を選んで新規作成スタート", note: "「シフト作成」→「＋新規」で提出受付を開始" },
              ].map((step, i) => (
                <li key={i} className="flex gap-3" data-testid={`setup-step-${i + 1}`}>
                  <span className="shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <div className="flex-1 pt-0.5">
                    <p className="text-sm font-bold text-slate-900 leading-tight">{step.title}</p>
                    <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">{step.note}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {/* 右: LINE サポート */}
          <div className="p-5 lg:p-6 flex flex-col justify-center gap-4">
            <div>
              <div className="text-[11px] font-bold text-[#06C755] tracking-widest mb-1">SUPPORT</div>
              <h3 className="text-base font-bold text-slate-900 leading-snug">操作で迷ったら<br />LINE で気軽に相談</h3>
              <p className="text-xs text-slate-500 mt-2 leading-relaxed">
                営業時間中であれば、その場で回答いたします。<br />
                初期設定からシフト作成のコツまでお気軽にどうぞ。
              </p>
            </div>
            <LineSupportBanner />
          </div>
        </div>
      </Card>

      {/* クイックスタート */}
      <Card className="p-5 bg-gradient-to-br from-blue-50 to-emerald-50 border-blue-200">
        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2 mb-3">
          <Sparkles className="w-5 h-5 text-blue-600" />
          クイックスタート（5分で読める）
        </h2>
        <StepList
          items={[
            "「シフト作成」を開き、右上の「＋新規」から月を作成（提出受付開始）",
            "スタッフが「希望提出」で ◎入れます／△必要なら／×入れません を入力",
            "ステップ②「配置マジック」で自動配置（5秒ほどで完成）",
            "ステップ③「日を調整する」でカレンダーから各日を微調整",
            "ステップ⑤「確定する」を押して、スタッフに公開",
          ]}
        />
        <TipBox tone="emerald">
          配置マジックは何度でもやり直せます。「確定」ロックを付けたスタッフは保持されます。
        </TipBox>
      </Card>

      {/* 知らないと損する Tips 5選 */}
      <Card className="p-5 bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200" data-testid="help-hidden-tips">
        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2 mb-1">
          <Lightbulb className="w-5 h-5 text-amber-500" />
          知らないと損する 6つの便利機能
        </h2>
        <p className="text-xs text-slate-600 mb-4">使う人が気付くと毎月のシフト作成がぐっと楽になる「地味スゴ」ポイントをまとめました。</p>

        <div className="space-y-3">
          <HiddenTip
            id="tip-limits"
            num={1}
            title="日付には ◎ なのに外されている？ それは上限ルール"
            body={
              <>
                配置マジックで「◎入れます」のはずなのに割り当てられない日があったら、それは <b>そのスタッフの月間／週間希望日数を超えた分</b> が自動で除外されているからです。
                スタッフ画面で本人が「今月20日まで／週4日まで」と設定していると、それを超えないように調整されます。
                <div className="text-[11px] text-slate-500 mt-1">設定場所：スタッフ管理 → 各スタッフの編集 →「月間希望」「週最大日数」</div>
              </>
            }
            where="配置マジック 実行後"
          />
          <HiddenTip
            id="tip-status"
            num={2}
            title="誰がどんな希望を出したか、一覧で丸見え"
            body={
              <>
                「スタッフ状況一覧」を開くと、各スタッフの提出状況 ◎ △ × 件数や、未提出かどうか、希望日数がひと目で分かります。
                提出忘れの人をワンクリックで <b>リマインド送信</b> できるので、催促の手間ゼロです。
              </>
            }
            where="サイドメニュー → スタッフ状況一覧"
          />
          <HiddenTip
            id="tip-day-coverage"
            num={3}
            title="日毎に「希望に対して配置できたか」が見える"
            body={
              <>
                「確定シフト」のカレンダー表示で各日をタップすると、その日「◎を出した人」「△を出した人」「実際に入った人」が並列で表示されます。
                <b>希望を出したのに入れなかった人</b> が一目で分かるので、次回のフォローが格段にやりやすくなります。
              </>
            }
            where="サイドメニュー → 確定シフト"
          />
          <HiddenTip
            id="tip-fulfillment"
            num={4}
            title="各スタッフの「希望の叶い具合」を可視化"
            body={
              <>
                スタッフ状況一覧の右側に、<b>叶い率（◎のうち何%入ったか）</b> が表示されます。
                同じ人が3ヶ月連続で叶い率が低いとモチベ低下のサインなので、優先的に配置するヒントになります。
              </>
            }
            where="サイドメニュー → スタッフ状況一覧（右側カラム）"
          />
          <HiddenTip
            id="tip-proxy"
            num={5}
            title="提出してくれないスタッフ分は「代理入力」できる"
            body={
              <>
                LINE 等で「3日、7日、15日OK」みたいに送られてきた場合、AIに文章をペーストすれば自動で希望に変換します。
                ステップ① 今月の準備の <b>「代理入力」</b> から、対象スタッフを選んで文章を貼るだけです。
              </>
            }
            where="シフト作成 → ステップ① → 代理入力"
          />
          <HiddenTip
            id="tip-fill"
            num={6}
            title="早く出した人はそのまま、遅れた人だけ追加で配置"
            body={
              <>
                ある程度シフトを組んだあとに、遅れて希望を出してくれたスタッフが居ても安心。
                配置マジックには 2 つのモードがあります。
                <ul className="list-disc pl-5 mt-1.5 space-y-0.5 text-[12px]">
                  <li><b>fill（空き埋め）</b>: 既に配置済みのスタッフは <b>そのまま動かさず</b>、足りない日に新しい希望者だけを追加</li>
                  <li><b>fresh（作り直し）</b>: 仮シフトを一旦クリアして、最新の希望状況でゼロから組み直し</li>
                </ul>
                <div className="text-[11px] text-slate-500 mt-1.5">
                  「確定」ロックを付けたスタッフは fresh でも守られます。マジックは <b>何度でも掛け直し</b> 可能です。
                </div>
              </>
            }
            where="配置マジック → モード選択"
          />
        </div>
      </Card>

      {/* シフト作成 5ステップ */}
      <Section icon={CalendarPlus} title="シフト作成 5ステップフロー" testId="help-mgr-shift-creation">
        <p>
          月のシフトを作る基本フロー。上部のステップバーで進行状況がわかります。前後の移動は自由にできます。
        </p>

        <div className="mt-3 space-y-3">
          <div className="border-l-4 border-blue-500 pl-3 py-1">
            <p className="font-semibold text-slate-900 text-sm">① 今月の準備</p>
            <p className="text-xs text-slate-600">
              休業日のチェック、曜日別の必要人数を設定。提出済み／未提出のスタッフを確認し、
              「リマインド」ボタンでまとめて通知できます。提出がない人には「代理入力」も可能（後述）。
            </p>
          </div>

          <div className="border-l-4 border-purple-500 pl-3 py-1">
            <p className="font-semibold text-slate-900 text-sm">② 配置マジック</p>
            <p className="text-xs text-slate-600">
              希望と必要人数を元に AI が自動配置。配置モードは 2 種類：
            </p>
            <ul className="text-xs text-slate-600 mt-1 space-y-0.5 list-disc list-inside">
              <li><b>希望優先</b>：◎入れますを最大限尊重（多めでもOK）</li>
              <li><b>必要人数厳守</b>：曜日別の必要人数で打ち切り</li>
            </ul>
            <p className="text-xs text-slate-600 mt-1">
              「fresh（作り直し）」と「fill（空き埋め）」を選択可能。「確定」ロック済みのスタッフは保持されます。
            </p>
          </div>

          <div className="border-l-4 border-emerald-500 pl-3 py-1">
            <p className="font-semibold text-slate-900 text-sm">③ 日を調整する</p>
            <p className="text-xs text-slate-600">
              カレンダーから日付を選び、右側パネルで微調整。
            </p>
            <ul className="text-xs text-slate-600 mt-1 space-y-0.5 list-disc list-inside">
              <li><b>入っている人</b>：時間調整、外す、「確定」ロック</li>
              <li><b>候補スタッフ</b>：◎／△／× タブで切り替え、「入れる」で追加</li>
              <li><b>検討依頼</b>：候補に対して「打診」を送信（次の章で詳しく）</li>
            </ul>
          </div>

          <div className="border-l-4 border-sky-500 pl-3 py-1">
            <p className="font-semibold text-slate-900 text-sm">④ スタッフ別設定状況</p>
            <p className="text-xs text-slate-600">
              全スタッフの希望日数 vs 配置日数を一覧でチェック。「日ごとを見る」で各スタッフの詳細も確認できます。
            </p>
          </div>

          <div className="border-l-4 border-rose-500 pl-3 py-1">
            <p className="font-semibold text-slate-900 text-sm">⑤ 確定する</p>
            <p className="text-xs text-slate-600">
              レビュー画面で最終確認 → 「確定する（公開する）」でスタッフに公開。
              公開後の変更も可能ですが、確定シフトは特別な扱いになります。
            </p>
          </div>
        </div>
      </Section>

      {/* 検討依頼 */}
      <Section icon={MessageSquare} title="検討依頼（打診型お願い）" testId="help-mgr-request">
        <p>
          「もしかしたらお願いするかも」のスタッフに、軽く打診を送る機能です。
          スタッフの返信を確認したうえで、最終的に入れるかどうかをマネージャが決めます。
        </p>
        <StepList
          items={[
            "ステップ③で日付を選び、候補スタッフから「検討依頼」ボタン",
            "ダイアログにメッセージが自動入力されるので、必要なら編集して送信",
            "スタッフが「承諾」「NG」「代替時間」を返信",
            "候補リストにバッジが表示される：✓検討OK／× NG／代替 HH:MM〜／依頼中",
            "「✓検討OK」のスタッフは行が緑にハイライト。必要なら「入れる」で正式追加",
          ]}
        />
        <TipBox tone="amber">
          承諾されても自動でシフトには入りません。必ずマネージャが「入れる」ボタンを押す必要があります。
          これにより、急な変更や重複を防げます。
        </TipBox>
      </Section>

      {/* 代理入力 */}
      <Section icon={Wand2} title="代理入力（AI 自然文パース）" testId="help-mgr-proxy">
        <p>
          未提出のスタッフから LINE などで届いたシフト希望を、そのまま貼り付けて AI に解析させる機能です。
        </p>
        <StepList
          items={[
            "ステップ①「未提出」一覧の「代理入力」ボタンをクリック",
            "送られてきたテキストを貼り付け（例：「土日休み、平日は時間あれば入ります」）",
            "AI が日付ごとの ◎／△／× に変換して表示",
            "内容を確認・修正してから「提出する」",
          ]}
        />
        <TipBox tone="blue">
          月の何日かを明示しなくても「土日」「平日」などの表現を理解します。
          ただし結果は必ず確認してください。
        </TipBox>
      </Section>

      {/* スタッフ管理 */}
      <Section icon={Users} title="スタッフ管理" testId="help-mgr-staff">
        <p>
          サイドバー「スタッフ管理」から、配下スタッフの追加・編集・退職処理ができます。
        </p>
        <StepList
          items={[
            "「＋新規登録」でスタッフ追加。ログインID／初期パスワード／対応できる配置を設定",
            "✏️アイコンで編集。配置の追加・希望日数・週上限なども設定可能",
            "🔑アイコンで「パスワード再発行」（スタッフがログインできなくなった時用）",
            "発行された 10桁の新パスワードはコピーして本人にお伝えください",
            "「配置マスタ」タブで店舗独自の配置（ホール／キッチン／レジなど）を編集可能",
          ]}
        />
        <TipBox tone="amber">
          パスワード再発行は元のパスワードに戻せません。必ず新パスワードを本人に伝えてください。
        </TipBox>
      </Section>

      {/* 確定シフト */}
      <Section icon={CalendarCheck} title="確定シフト" testId="help-mgr-confirmed">
        <p>
          公開済みのシフトを一覧／印刷／PDFで確認できます。スタッフからのシフト交換依頼もここで承認します。
        </p>
      </Section>

      {/* よくある質問 */}
      <Section icon={HelpCircle} title="よくある質問" testId="help-mgr-faq">
        <details className="border border-slate-200 rounded-md p-3">
          <summary className="cursor-pointer text-sm font-semibold text-slate-800">配置マジックをやり直したら、調整した内容が消えました</summary>
          <p className="text-xs text-slate-600 mt-2">
            「fresh（作り直し）」は仮シフトを全部クリアします。残したいスタッフには事前に「確定」ロックを付けてください。
            「fill（空き埋め）」なら既存配置は保持されます。
          </p>
        </details>
        <details className="border border-slate-200 rounded-md p-3">
          <summary className="cursor-pointer text-sm font-semibold text-slate-800">確定後のシフトを変更したい</summary>
          <p className="text-xs text-slate-600 mt-2">
            ステップ③に戻って編集できます。確定後の変更はスタッフに通知が届きます。
          </p>
        </details>
        <details className="border border-slate-200 rounded-md p-3">
          <summary className="cursor-pointer text-sm font-semibold text-slate-800">スタッフのログインURLを知りたい</summary>
          <p className="text-xs text-slate-600 mt-2">
            <code className="bg-slate-100 px-1 rounded">https://shiftmagic.jp/[店舗ID]/login</code> です。
            店舗IDは管理者発行のメール、または現在URLで確認できます。
          </p>
        </details>
      </Section>
    </div>
  );
}

/* =========================================================
 * スタッフ向けマニュアル
 * ========================================================= */
function StaffGuide() {
  return (
    <div className="space-y-8" data-testid="help-staff">
      <Card className="p-5 bg-gradient-to-br from-emerald-50 to-blue-50 border-emerald-200">
        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2 mb-3">
          <Sparkles className="w-5 h-5 text-emerald-600" />
          クイックスタート（3分で読める）
        </h2>
        <StepList
          items={[
            "初回ログイン後、表示名・出勤可能時間・希望日数を設定",
            "下メニュー「希望提出」を開き、各日に ◎／△／× を入力",
            "提出が終わったら、店長が配置を組んでくれます",
            "シフトが公開されたら下メニュー「シフト」で自分の予定を確認",
          ]}
        />
        <TipBox tone="emerald">
          希望は何度でも修正できます（提出受付中の場合）。
        </TipBox>
      </Card>

      <Section icon={ClipboardList} title="希望提出のしかた" testId="help-staff-request">
        <p>下メニュー「希望提出」から、各日の希望タイプを選びます。</p>
        <div className="space-y-2 mt-2">
          <div className="flex items-start gap-2 text-sm">
            <span className="shrink-0 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-300 font-semibold text-xs">◎ 入れます</span>
            <span className="text-slate-700">通常出勤OK。希望時間を入力できます。</span>
          </div>
          <div className="flex items-start gap-2 text-sm">
            <span className="shrink-0 px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 border border-sky-300 font-semibold text-xs">○ 必要なら入れます</span>
            <span className="text-slate-700">人が足りない時のみ出勤OK。お願い候補。</span>
          </div>
          <div className="flex items-start gap-2 text-sm">
            <span className="shrink-0 px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-300 font-semibold text-xs">× 入れません</span>
            <span className="text-slate-700">出勤不可。理由は任意。</span>
          </div>
        </div>
        <TipBox tone="blue">
          全日「◎入れます」を一括設定して、入れない日だけ × にすると速いです。
        </TipBox>
      </Section>

      <Section icon={CalendarCheck} title="自分のシフトを確認" testId="help-staff-myshifts">
        <p>下メニュー「シフト」から、公開された自分のシフトを確認できます。</p>
        <ul className="text-xs text-slate-600 list-disc list-inside space-y-0.5">
          <li>日付・時間・配置（ホール／キッチンなど）が表示</li>
          <li>「変更したい」ボタンから店長に変更依頼を送信できます</li>
          <li>確定前の仮シフトは黄色、確定済みは緑色で表示</li>
        </ul>
      </Section>

      <Section icon={MessageSquare} title="検討依頼が届いたら" testId="help-staff-message">
        <p>店長から「検討依頼」（出勤打診）が届くことがあります。</p>
        <StepList
          items={[
            "下メニュー「メッセージ」に通知バッジが付きます",
            "店長とのトークを開き、「出勤の検討依頼」を確認",
            "「承諾」「NG」「代替時間」のいずれかで返信",
            "返信後、店長が最終的にシフトに入れるか決定します",
          ]}
        />
        <TipBox tone="amber">
          承諾しても自動でシフトには入りません。店長から再度連絡が来るのを待ってください。
        </TipBox>
      </Section>

      <Section icon={UserCog} title="個人設定の変更" testId="help-staff-setup">
        <p>下メニュー「設定」から以下が変更できます。</p>
        <ul className="text-xs text-slate-600 list-disc list-inside space-y-0.5">
          <li>表示名（店長やシフト画面に表示される名前）</li>
          <li>標準の出勤時間（毎月の希望提出時の初期値）</li>
          <li>月間希望日数 / 月間上限 / 週上限</li>
          <li>希望曜日</li>
        </ul>
      </Section>

      <Section icon={HelpCircle} title="よくある質問" testId="help-staff-faq">
        <details className="border border-slate-200 rounded-md p-3">
          <summary className="cursor-pointer text-sm font-semibold text-slate-800">パスワードを忘れました</summary>
          <p className="text-xs text-slate-600 mt-2">
            店長にパスワード再発行を依頼してください。新しい 10桁のパスワードが発行されます。
          </p>
        </details>
        <details className="border border-slate-200 rounded-md p-3">
          <summary className="cursor-pointer text-sm font-semibold text-slate-800">希望日数を超えて配置されました</summary>
          <p className="text-xs text-slate-600 mt-2">
            「希望日数」は目安です。店舗の事情で超えることもあります。
            問題があれば「変更したい」ボタンから店長へ依頼してください。
          </p>
        </details>
        <details className="border border-slate-200 rounded-md p-3">
          <summary className="cursor-pointer text-sm font-semibold text-slate-800">急に休まないといけません</summary>
          <p className="text-xs text-slate-600 mt-2">
            「シフト」画面の該当日から「変更したい」ボタンで依頼を送れます。
            急ぎの場合は店長に直接連絡してください。
          </p>
        </details>
      </Section>
    </div>
  );
}

/* =========================================================
 * 管理者向けマニュアル
 * ========================================================= */
function AdminGuide() {
  return (
    <div className="space-y-8" data-testid="help-admin">
      <Card className="p-5 bg-gradient-to-br from-slate-50 to-blue-50 border-slate-200">
        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2 mb-3">
          <Sparkles className="w-5 h-5 text-blue-600" />
          管理者業務クイックフロー
        </h2>
        <StepList
          items={[
            "「資料請求リード」で新規申込を確認",
            "良いリードに「アカウント発行」を押す → 自動メール送信",
            "「お問い合わせ」で店舗からの質問・要望を確認・対応",
            "「クーポン管理」でキャンペーンコードを管理",
          ]}
        />
      </Card>

      <Section icon={Mail} title="資料請求リード管理" testId="help-admin-leads">
        <p>LP からの資料請求がリスト化されます。</p>
        <StepList
          items={[
            "新規リードの内容（会社名・人数・業種）を確認",
            "「アカウント発行」を押すと、新しい manager アカウントが作られ、Resend 経由で自動メール送信",
            "メールには ログインID・初期パスワード・店舗別ログインURL が含まれる",
            "クーポンを適用したい場合は、リード詳細でコードを入力してから発行",
          ]}
        />
        <TipBox tone="amber">
          アカウント発行は取り消せません。誤発行に注意してください（メールアドレスの確認推奨）。
        </TipBox>
      </Section>

      <Section icon={Inbox} title="お問い合わせ管理" testId="help-admin-inquiries">
        <p>店舗内ユーザー（manager / staff）から送られた問い合わせを管理します。</p>
        <ul className="text-xs text-slate-600 list-disc list-inside space-y-0.5">
          <li>緊急度・カテゴリ・ステータス（未対応／対応中／対応済）でフィルタ</li>
          <li>各問い合わせを開いて、メモを残せます</li>
          <li>新着問い合わせは Resend で管理者メールにも届きます</li>
        </ul>
      </Section>

      <Section icon={Ticket} title="クーポン管理" testId="help-admin-coupons">
        <StepList
          items={[
            "「＋新規クーポン」でコード／表示ラベル／有効期限を登録",
            "アカウント発行時に該当コードを適用すると、店舗側に coupon_applied=true がセットされる",
            "将来の Stripe Billing 統合時に、coupon_applied 店舗は 50% OFF が適用予定",
            "使用回数（used_count）は自動カウント",
          ]}
        />
      </Section>

      <Section icon={UserCog} title="アカウント設定" testId="help-admin-profile">
        <p>管理者自身のログインID／パスワードを変更できます。</p>
        <ul className="text-xs text-slate-600 list-disc list-inside space-y-0.5">
          <li>「ログインID変更」「パスワード変更」</li>
          <li>「未発行リードのパージ」（古いリードを一括削除）</li>
        </ul>
      </Section>
    </div>
  );
}

/* =========================================================
 * Help page (with role-based default tab)
 * ========================================================= */
export default function Help() {
  const { user } = useAuth();
  const role = user?.role;
  const { hash } = useLocation();

  // デフォルトタブ: ログインしているユーザーのロールに合わせる
  const [tab, setTab] = useState(
    role === "admin" ? "admin" : role === "staff" ? "staff" : "manager",
  );

  // ハッシュ付き遷移時: 該当 Tip までスクロール + ハイライト
  useEffect(() => {
    if (!hash) return;
    const id = hash.replace(/^#/, "");
    if (!id) return;
    // タブ切り替え後に DOM が描画されるのを待つ
    const timer = setTimeout(() => {
      const el = document.getElementById(id);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.classList.add("ring-2", "ring-amber-400", "ring-offset-2");
      setTimeout(() => el.classList.remove("ring-2", "ring-amber-400", "ring-offset-2"), 2400);
    }, 250);
    return () => clearTimeout(timer);
  }, [hash, tab]);

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto" data-testid="help-page">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
          <HelpCircle className="w-7 h-7 text-blue-600" />
          使い方ガイド
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          やさまるシフトの基本操作と便利な機能を、役割別にまとめています。
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6">
          {role !== "admin" && (
            <>
              <TabsTrigger value="manager" data-testid="help-tab-manager">店長・マネージャ</TabsTrigger>
              <TabsTrigger value="staff" data-testid="help-tab-staff">スタッフ</TabsTrigger>
            </>
          )}
          {role === "admin" && (
            <TabsTrigger value="admin" data-testid="help-tab-admin">管理者</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="manager"><ManagerGuide /></TabsContent>
        <TabsContent value="staff"><StaffGuide /></TabsContent>
        <TabsContent value="admin"><AdminGuide /></TabsContent>
      </Tabs>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3">
        <LineSupportBanner />
        <Card className="p-4 bg-slate-50 border-slate-200 flex items-start gap-2">
          <Lightbulb className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
          <span className="text-sm text-slate-700 leading-relaxed">
            メールでのご質問は「お問い合わせ」フォームから運営に直接お送りいただけます。ご要望や不具合の報告もこちらからどうぞ。
          </span>
        </Card>
      </div>
    </div>
  );
}
